import { Link, useNavigate } from '@remix-run/react';
import { Button } from '~/design-system/elements/Button/Button';
import { cn } from "~/lib/utils";
import styles from './Header.module.css';
import { useState, ReactNode } from 'react';
import { Alert } from '~/design-system/components/Alert/Alert';
import type { User } from '@supabase/supabase-js';
import { formatStoryTimestamp } from '~/utils/date-utils';
import { UserHoverCard } from '~/design-system/components/Story/header/UserHoverCard';
import { LogoUI } from '~/design-system/elements/Logo/LogoUI';
import { UpgradeButtonUI } from '~/design-system/components/UpgradeButton/UpgradeButtonUI';
import { AvatarDropdownUI } from '~/design-system/components/AvatarDropdown/AvatarDropdownUI';
import { SubscriptionTier } from '~/queries/subscription';
import { ShareDropdown } from './ShareDropdown';
import { DeckVisibility } from '~/types/project';
import { DeckIcon } from '~/design-system/elements/DeckIcon/DeckIcon';

type HeaderBackgroundVariant = 'card' | 'subtle' | 'muted' | 'transparent';

interface HeaderUIProps {
  displayUser?: User | null;
  isLoading?: boolean;
  minimal?: boolean;
  simplified?: boolean;
  backgroundVariant?: HeaderBackgroundVariant;
  isStoryPage?: boolean;
  showUpgradeButton?: boolean;
  /** Hide the avatar/account menu in the header (e.g. account pages that already have the profile sidebar). */
  showAvatar?: boolean;
  showBackButton?: boolean;
  onBackClick?: () => void;
  onShare?: () => void;
  onEdit?: () => void;
  avatarComponent?: ReactNode;
  logoComponent?: ReactNode;
  upgradeButtonComponent?: ReactNode;
  deckId?: string;
  projectId?: string;
  conversationId?: string;
  currentVisibility?: DeckVisibility;
  isAuthenticated?: boolean;
  publicStory?: {
    title: string;
    createdAt: string;
    author: {
      id: string;
      firstName: string;
      lastName: string;
      email: string;
      position?: string;
      avatar?: string;
    };
    showAnnotations: boolean;
    onToggleAnnotations: () => void;
  };
}

const getBackgroundVariantClass = (variant: HeaderBackgroundVariant): string => {
  switch (variant) {
    case 'card':
      return styles.headerBackgroundCard;
    case 'subtle':
      return styles.headerBackgroundSubtle;
    case 'muted':
      return styles.headerBackgroundMuted;
    case 'transparent':
      return styles.headerBackgroundTransparent;
    default:
      return styles.headerBackgroundTransparent;
  }
};

const UserSection = ({ 
  isLoading, 
  displayUser,
  avatarComponent
}: { 
  isLoading: boolean; 
  displayUser: User | null;
  avatarComponent?: ReactNode;
}) => {
  if (isLoading) {
    return <span>Loading...</span>;
  }
  
  if (displayUser) {
    return avatarComponent ? (
      <>{avatarComponent}</>
    ) : (
      <AvatarDropdownUI 
        onProfileClick={() => console.log('Profile clicked')}
        onPricingClick={() => console.log('Pricing clicked')}
        onBillingClick={() => console.log('Billing clicked')}
        onIntegrationsClick={() => console.log('Integrations clicked')}
        onSettingsClick={() => console.log('Settings clicked')}
        onSignOutClick={() => console.log('Sign out clicked')}
      />
    );
  }
  
  return (
    <Link to="/sign-in">
      <Button variant="soft" size="sm" className={styles.buttonFixed}>
        Sign In
      </Button>
    </Link>
  );
};

export const HeaderUI = ({ 
  displayUser = null,
  isLoading = false,
  minimal = false, 
  simplified = false,
  backgroundVariant = 'transparent',
  isStoryPage = false,
  showUpgradeButton = true,
  showAvatar = true,
  showBackButton = false,
  onBackClick,
  onShare,
  onEdit,
  avatarComponent,
  logoComponent,
  upgradeButtonComponent,
  deckId,
  projectId,
  conversationId,
  currentVisibility = DeckVisibility.PRIVATE,
  isAuthenticated = false,
  publicStory
}: HeaderUIProps) => {
  const [showCopyAlert, setShowCopyAlert] = useState(false);
  const navigate = useNavigate();
  
  const handleShare = () => {
    if (onShare) {
      onShare();
    } else {
      navigator.clipboard.writeText(window.location.href);
    }
    setShowCopyAlert(true);
    setTimeout(() => setShowCopyAlert(false), 4000);
  };

  const handleEdit = () => {
    if (onEdit) {
      onEdit();
    } else if (deckId) {
      navigate(`/deck/${deckId}`);
    }
  };

  const handleLogoClick = () => {
    if (isAuthenticated) {
      navigate('/dashboard');
    } else {
      window.location.href = 'https://usedeck.ai';
    }
  };
  
  if (simplified && publicStory) {
    return (
      <header 
        className={cn(
          styles.header,
          styles.publicDeckHeaderBackground
        )}
      >
        <div className={styles.publicDeckHeader}>
          <div 
            className={styles.publicDeckLeftContainer}
            onClick={handleLogoClick}
            style={{ cursor: 'pointer' }}
          >
            <LogoUI size={28} variant="minimal" color="white" />
          </div>
          
          <div className={styles.centerSection}>
            <div className={styles.publicStoryInfo}>
              <UserHoverCard author={publicStory.author}>
                <img 
                  src={publicStory.author.avatar || '/user-avatar.png'} 
                  alt={`${publicStory.author.firstName} ${publicStory.author.lastName}`}
                  className={styles.authorAvatar}
                />
              </UserHoverCard>
              <span className={styles.storyTitle}>{publicStory.title}</span>
              <span className={styles.timestamp}>
                {formatStoryTimestamp(publicStory.createdAt)}
              </span>
            </div>
          </div>
          
          <div className={styles.publicDeckRightContainer}>
            <Button 
              variant="ghostInverted"
              size="xs"
              onClick={handleEdit}
            >
              Edit
            </Button>
            <Button 
              variant="ghostInverted"
              size="xs"
              onClick={() => {
                if (typeof window !== 'undefined') {
                  navigator.clipboard.writeText(window.location.href);
                }
              }}
            >
              Share
            </Button>
          </div>
        </div>
      </header>
    );
  }
  
  if (simplified) {
    return (
      <header 
        className={cn(
          styles.header,
          styles.headerFixed,
          getBackgroundVariantClass(backgroundVariant)
        )}
      >
        <div className={styles.headerContainer}>
          {showBackButton ? (
            <button type="button" 
              onClick={onBackClick || (() => navigate('/dashboard'))}
              className={styles.backButton}
            >
              <DeckIcon name="arrow-left" size={24} />
            </button>
          ) : (
            logoComponent || <LogoUI size={32} className={styles.logoContainer} />
          )}
          
          <div className={styles.rightSection}>
            {showUpgradeButton && (
              upgradeButtonComponent || (
                <UpgradeButtonUI 
                  size="sm" 
                  currentPlan={SubscriptionTier.FREE}
                  onSeePlans={() => console.log('See plans clicked')}
                />
              )
            )}
            {showAvatar && <UserSection isLoading={isLoading} displayUser={displayUser} avatarComponent={avatarComponent} />}
          </div>
        </div>
      </header>
    );
  }

  return (
    <header
      className={cn(
        styles.header,
        styles.headerFixed,
        minimal && styles.minimalHeader,
        getBackgroundVariantClass(backgroundVariant)
      )}
    >
      <div className={styles.headerContainer}>
        {logoComponent || <LogoUI size={32} className={styles.logoContainer} />}
        
        <div className={styles.rightSection}>
          {showUpgradeButton && (
            upgradeButtonComponent || (
              <UpgradeButtonUI 
                size="sm" 
                currentPlan={SubscriptionTier.FREE}
                onSeePlans={() => console.log('See plans clicked')}
              />
            )
          )}
          
          {isStoryPage && (
            <div className={styles.shareContainer}>
              <Button 
                variant="default" 
                className={styles.buttonFixed}
                leadIcon="share-2-line"
                onClick={handleShare}
              >
                Share
              </Button>
              {showCopyAlert && (
                <Alert className={styles.copyAlert}>
                  Link copied to clipboard
                </Alert>
              )}
            </div>
          )}
          
          {showAvatar && <UserSection isLoading={isLoading} displayUser={displayUser} avatarComponent={avatarComponent} />}
        </div>
      </div>
    </header>
  );
};

